import * as THREE from 'three';

export interface TerrainOptions {
  size: number;
  segments: number;
  /** Analytic height function — also used for walking, so they always agree. */
  height: (x: number, z: number) => number;
  material: THREE.Material;
  /** Optional 0..1 snow coverage per point (adds an `aSnow` attribute). */
  snowMask?: (x: number, z: number) => number;
}

export interface Terrain {
  mesh: THREE.Mesh;
  groundHeight: (x: number, z: number) => number;
}

/**
 * Height-field terrain. UVs stay the default planar [0,1] mapping — the paint
 * system projects stamps by world-space distance, so slopes paint without
 * stretching regardless of UV density.
 */
export function createTerrain(options: TerrainOptions): Terrain {
  const { size, segments, height, material, snowMask } = options;
  const geometry = new THREE.PlaneGeometry(size, size, segments, segments);
  geometry.rotateX(-Math.PI / 2);

  const pos = geometry.getAttribute('position') as THREE.BufferAttribute;
  const snow = snowMask ? new Float32Array(pos.count) : null;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const z = pos.getZ(i);
    pos.setY(i, height(x, z));
    if (snow && snowMask) snow[i] = snowMask(x, z);
  }
  pos.needsUpdate = true;
  geometry.computeVertexNormals();
  if (snow) geometry.setAttribute('aSnow', new THREE.BufferAttribute(snow, 1));

  const mesh = new THREE.Mesh(geometry, material);
  mesh.receiveShadow = true;
  return { mesh, groundHeight: height };
}
